import {
  isRouteErrorResponse,
  Links,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
  useLocation,
} from 'react-router';
import type { Route } from './+types/root';
import { canonicalUrl, SITE_URL } from '@/lib/seo';
import './app.css';

export const links: Route.LinksFunction = () => [
  { rel: 'preconnect', href: 'https://fonts.googleapis.com' },
  {
    rel: 'preconnect',
    href: 'https://fonts.gstatic.com',
    crossOrigin: 'anonymous',
  },
  {
    rel: 'stylesheet',
    href: 'https://fonts.googleapis.com/css2?family=Inter:ital,opsz,wght@0,14..32,100..900;1,14..32,100..900&display=swap',
  },
];

export function meta({}: Route.MetaArgs) {
  const title = 'Create Gamified Cybersecurity Training for Teams & Students | Simulations Labs';
  const description =
    'Simulations Labs is a no-code platform, offering entities and individuals the power to build labs and CTF competitions effortlessly with access to a comprehensive challenge library';
  const imageUrl = `${SITE_URL}/og-image.png`;
  const url = SITE_URL;

  return [
    { title },
    { name: 'description', content: description },
    {
      name: 'keywords',
      content:
        'Simulations Labs, CTF, cybersecurity, training, labs, competition, platform, software, no-code, challenge library, gamified, training, teams, students',
    },
    { name: 'author', content: 'Simulations Labs' },
    { rel: 'icon', href: `${SITE_URL}/logo.svg` },

    // Open Graph tags for general social sharing
    { property: 'og:title', content: title },
    { property: 'og:description', content: description },
    { property: 'og:url', content: url },
    { property: 'og:image', content: imageUrl },
    { property: 'og:image:secure_url', content: imageUrl },
    { property: 'og:image:width', content: '1200' },
    { property: 'og:image:height', content: '630' },
    { property: 'og:type', content: 'website' },
    { property: 'og:site_name', content: 'Simulations Labs' },

    // Twitter Card tags
    { name: 'twitter:card', content: 'summary_large_image' },
    { name: 'twitter:title', content: title },
    { name: 'twitter:description', content: description },
    { name: 'twitter:image', content: imageUrl },
    { name: 'twitter:url', content: url },
  ];
}

export function Layout({ children }: { children: React.ReactNode }) {
  // One canonical for every route, derived from the path so that tracking
  // params never fork a page into duplicate URLs.
  const { pathname, search } = useLocation();
  const canonical = canonicalUrl(pathname, search);

  // Get Google Tag Manager ID from environment variables
  const gtmId = import.meta.env.VITE_GOOGLE_TAG_MANAGER_ID || '';
  const freshdeskToken = (import.meta.env.VITE_FRESHDESK_TOKEN as string | undefined)?.trim() || '';
  const freshdeskWidgetId =
    (import.meta.env.VITE_FRESHDESK_WIDGET_ID as string | undefined)?.trim() || '';

  return (
    <html lang="en">
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <Meta />
        <Links />
        <link rel="canonical" href={canonical} />
        <script
          dangerouslySetInnerHTML={{
            __html: `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
})(window,document,'script','dataLayer','${gtmId}');`,
          }}
        />
        {freshdeskToken && freshdeskWidgetId ? (
          <script
            dangerouslySetInnerHTML={{
              __html: `function initFreshdesk() {
  window.fdWidget.init({
    token: "${freshdeskToken}",
    host: "https://simulationslabs-help.freshdesk.com",
    widgetId: "${freshdeskWidgetId}"
  });
}

function initialize(i,t){var e;i.getElementById(t)?initFreshdesk():((e=i.createElement("script")).id=t,e.async=!0,e.src="https://simulationslabs-help.freshdesk.com/webchat/js/widget.js",e.onload=initFreshdesk,i.head.appendChild(e))}function initiateCall(){initialize(document,"Freshdesk-js-sdk")}window.addEventListener?window.addEventListener("load",initiateCall,!1):window.attachEvent("load",initiateCall,!1);`,
            }}
          />
        ) : null}
      </head>
      <body>
        <noscript>
          <iframe
            src={`https://www.googletagmanager.com/ns.html?id=${gtmId}`}
            height="0"
            width="0"
            style={{ display: 'none', visibility: 'hidden' }}
          ></iframe>
        </noscript>
        {children}
        <ScrollRestoration />
        <Scripts />
      </body>
    </html>
  );
}

export default function App() {
  return <Outlet />;
}

export function ErrorBoundary({ error }: Route.ErrorBoundaryProps) {
  let message = 'Oops!';
  let details = 'An unexpected error occurred.';
  let stack: string | undefined;

  if (isRouteErrorResponse(error)) {
    message = error.status === 404 ? '404' : 'Error';
    details =
      error.status === 404 ? 'The requested page could not be found.' : error.statusText || details;
  } else if (import.meta.env.DEV && error && error instanceof Error) {
    details = error.message;
    stack = error.stack;
  }

  return (
    <main className="container mx-auto p-4 pt-16">
      <h1>{message}</h1>
      <p>{details}</p>
      {stack && (
        <pre className="w-full overflow-x-auto p-4">
          <code>{stack}</code>
        </pre>
      )}
    </main>
  );
}
